import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const AuditLogExporter = ({ onExportAuditLog }) => {
  const [exportConfig, setExportConfig] = useState({
    dateRange: 'last30days',
    startDate: '',
    endDate: '',
    eventTypes: [],
    userRoles: [],
    format: 'csv',
    includeMetadata: true
  });

  const dateRangeOptions = [
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'last90days', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Date Range' }
  ];

  const eventTypeOptions = [
    { value: 'document_upload', label: 'Document Upload' },
    { value: 'document_sign', label: 'Document Signature' },
    { value: 'user_login', label: 'User Login' },
    { value: 'transaction_create', label: 'Transaction Creation' },
    { value: 'status_change', label: 'Status Changes' },
    { value: 'permission_change', label: 'Permission Changes' }
  ];

  const userRoleOptions = [
    { value: 'lawyer', label: 'Lawyers' },
    { value: 'engineer', label: 'Engineers' },
    { value: 'notary', label: 'Notaries' },
    { value: 'owner', label: 'Property Owners' },
    { value: 'admin', label: 'Administrators' }
  ];

  const formatOptions = [
    { value: 'csv', label: 'CSV Format' },
    { value: 'json', label: 'JSON Format' },
    { value: 'excel', label: 'Excel Format' }
  ];

  const handleConfigChange = (key, value) => {
    setExportConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleEventTypeChange = (eventType, checked) => {
    setExportConfig(prev => ({
      ...prev,
      eventTypes: checked 
        ? [...prev?.eventTypes, eventType]
        : prev?.eventTypes?.filter(type => type !== eventType)
    }));
  };

  const handleUserRoleChange = (role, checked) => {
    setExportConfig(prev => ({
      ...prev,
      userRoles: checked 
        ? [...prev?.userRoles, role]
        : prev?.userRoles?.filter(r => r !== role)
    }));
  };

  const handleExport = () => {
    onExportAuditLog(exportConfig);
  };

  const recentExports = [
    {
      id: 1,
      name: "Audit Log Export - September 2025",
      date: "2025-09-20",
      size: "2.4 MB",
      format: "CSV",
      status: "completed"
    },
    {
      id: 2,
      name: "Document Activity Log - Q3 2025",
      date: "2025-09-15",
      size: "1.8 MB",
      format: "Excel",
      status: "completed"
    },
    {
      id: 3,
      name: "User Activity Export - August 2025",
      date: "2025-09-10",
      size: "3.1 MB",
      format: "JSON",
      status: "processing"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Export Configuration */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <Icon name="Shield" size={20} className="text-warning" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Audit Log Export</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="space-y-4">
            <Select
              label="Date Range"
              options={dateRangeOptions}
              value={exportConfig?.dateRange}
              onChange={(value) => handleConfigChange('dateRange', value)}
            />

            {exportConfig?.dateRange === 'custom' && (
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Start Date"
                  type="date"
                  value={exportConfig?.startDate}
                  onChange={(e) => handleConfigChange('startDate', e?.target?.value)}
                />
                <Input
                  label="End Date"
                  type="date"
                  value={exportConfig?.endDate}
                  onChange={(e) => handleConfigChange('endDate', e?.target?.value)}
                />
              </div>
            )}

            <Select
              label="Export Format"
              options={formatOptions}
              value={exportConfig?.format}
              onChange={(value) => handleConfigChange('format', value)}
            />

            <Checkbox
              label="Include Metadata & System Information"
              checked={exportConfig?.includeMetadata}
              onChange={(e) => handleConfigChange('includeMetadata', e?.target?.checked)}
            />
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-3 block">Event Types</label>
              <div className="space-y-2">
                {eventTypeOptions?.map((option) => (
                  <Checkbox
                    key={option?.value}
                    label={option?.label}
                    checked={exportConfig?.eventTypes?.includes(option?.value)}
                    onChange={(e) => handleEventTypeChange(option?.value, e?.target?.checked)}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-3 block">User Roles</label>
              <div className="space-y-2">
                {userRoleOptions?.map((option) => (
                  <Checkbox
                    key={option?.value}
                    label={option?.label}
                    checked={exportConfig?.userRoles?.includes(option?.value)}
                    onChange={(e) => handleUserRoleChange(option?.value, e?.target?.checked)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Export will include blockchain timestamps and verification data
          </div>
          <Button
            variant="warning"
            onClick={handleExport}
            iconName="Download"
            iconPosition="left"
          >
            Export Audit Log
          </Button>
        </div>
      </div>
      {/* Recent Exports */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Recent Exports</h3>
          <Button variant="ghost" size="sm" iconName="RefreshCw" iconPosition="left">
            Refresh
          </Button>
        </div>

        <div className="space-y-4">
          {recentExports?.map((exportItem) => (
            <div key={exportItem?.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={16} className="text-primary" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground">{exportItem?.name}</h4>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground mt-1">
                    <span>Created: {new Date(exportItem.date)?.toLocaleDateString()}</span>
                    <span>•</span>
                    <span>Size: {exportItem?.size}</span>
                    <span>•</span>
                    <span>Format: {exportItem?.format}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <span className={`text-xs px-2 py-1 rounded-full ${
                  exportItem?.status === 'completed' 
                    ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                }`}>
                  {exportItem?.status}
                </span>
                {exportItem?.status === 'completed' && (
                  <Button variant="ghost" size="sm" iconName="Download" />
                )}
                <Button variant="ghost" size="sm" iconName="Trash2" className="text-error hover:text-error" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AuditLogExporter;